import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FeatureFlags {
  [key: string]: boolean;
}

interface FeatureFlagContextType {
  flags: FeatureFlags;
  loading: boolean;
  /** Check if a feature is enabled. Returns defaultValue (true) if flag not found. */
  isEnabled: (key: string, defaultValue?: boolean) => boolean;
  /** Force refresh flags from database */
  refresh: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({
  flags: {},
  loading: true,
  isEnabled: () => true,
  refresh: async () => {},
});

export const useFeatureFlag = () => useContext(FeatureFlagContext);

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('flag_key, is_enabled');

      if (error) throw error;

      const map: FeatureFlags = {};
      data?.forEach((f) => {
        map[f.flag_key] = f.is_enabled;
      });
      setFlags(map);
    } catch (e) {
      console.error('Failed to load feature flags:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isEnabled = useCallback(
    (key: string, defaultValue = true): boolean => {
      if (loading) return defaultValue; // While loading, assume enabled
      return flags[key] ?? defaultValue;
    },
    [flags, loading],
  );

  return (
    <FeatureFlagContext.Provider value={{ flags, loading, isEnabled, refresh: load }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
